//dependencies
import { ObjectId } from "bson";
import React, { ReactNode, useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

import { faInfoCircle } from "@performyard/fontawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
//components
import { FontWeight, TextSize } from "@performyard/py-components/src/theme";

import TruncatedText from "../../../Text/TruncatedText";
import Tooltip from "../../../Tooltip";
import InputWrapper from "../../InputWrapper";

export type RatingScaleOption = {
	value: number | string | null;
	text: string;
	description?: string;
};

type RatingScaleInputProps = {
	/**
	 * The value of the input
	 */
	value?: RatingScaleOption["value"];
	/**
	 * The options of the input
	 */
	options: RatingScaleOption[];
	/**
	 * The function to call when the input value changes
	 */
	onChange: (value: RatingScaleOption["value"]) => void;
	/**
	 * The label of the input
	 */
	label?: string;
	/**
	 * size of the label
	 */
	labelSize?: TextSize;
	/**
	 * weight of the label
	 */
	labelWeight?: FontWeight;
	/**
	 * subtitle
	 */
	subtitle?: string | ReactNode;
	/**
	 * The error message of the input
	 */
	errorMessage?: string;
	/**
	 * The class name of the input
	 */
	className?: string;
	/**
	 * testid
	 */
	testid?: string;
};

/**
 * Rating scale input component
 *
 * This component allows inputting answers to a rating scale type question. These allow one of a group of options to be selected, and
 * are treated semantically as radio buttons. Each option includes a value, a text and an optional description. The value is the value
 * used to identify the rating scale option, text is displayed within the selectable option and the description (if provided) is displayed
 * in a tooltip when hovering over the 'i' icon.
 *
 * Label, error message, and subtitle support is included, which will wrap the collection of options in a labelled input group.
 */
const RatingScaleInput = ({
	value,
	options,
	onChange,
	label,
	labelSize = "paragraph-1",
	labelWeight = "semi-bold",
	subtitle = null,
	errorMessage,
	className = "",
	testid = "rating-scale-input",
}: RatingScaleInputProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const { width = 0 } = useResizeObserver<HTMLDivElement>({
		ref,
	});

	const parseValue = (value: string | null): number | string | null => {
		if (ObjectId.isValid(value ?? "")) {
			return value;
		} else if (isNaN(parseFloat(value ?? ""))) {
			return value;
		}
		return parseFloat(value ?? "");
	};

	const anyDescriptions = options.some((option) => option.description);

	return (
		<InputWrapper
			label={label}
			errorMessage={errorMessage}
			labelSize={labelSize}
			labelWeight={labelWeight}
			inputId="rating-scale-input"
		>
			{subtitle}
			<div
				role="radiogroup"
				aria-labelledby="rating-scale-input"
				aria-describedby="rating-scale-input"
				data-testid={testid}
				className={`align-start flex flex-col gap-2 ${className}`}
			>
				{options.map((option, index) => {
					const isChecked = value === option.value;
					return (
						<label htmlFor={`${option.value}-${index}`} key={option.value} className="flex h-[32px] gap-2">
							<div
								ref={ref}
								className={`flex h-full w-full items-center rounded px-2 ${isChecked ? "bg-primary-5 text-neutral-0" : "cursor-pointer bg-neutral-1 hover:bg-neutral-2"}`}
							>
								<input
									id={`${option.value}-${index}`}
									type="radio"
									value={option.value ?? ""}
									style={{ display: "none" }}
									onChange={(e) => {
										const parsedValue = parseValue(e.target.value);
										onChange(e.target.value.length > 0 ? parsedValue : null);
									}}
									checked={isChecked}
									aria-label={option.text}
									aria-checked={isChecked}
								/>
								<TruncatedText
									revealed={true}
									text={option.text}
									size="paragraph-3"
									weight="regular"
									maxWidth={width - 16}
								/>
							</div>
							{anyDescriptions && (
								<div className="flex h-full w-4 items-center justify-center">
									{option.description && (
										<Tooltip message={option.description}>
											<div className="text-neutral-3 hover:text-neutral-5">
												<FontAwesomeIcon icon={faInfoCircle} />
											</div>
										</Tooltip>
									)}
								</div>
							)}
						</label>
					);
				})}
			</div>
		</InputWrapper>
	);
};

export default RatingScaleInput;
